import { type ReactElement, useEffect, useId, useState } from "react";

import { Text } from "@backstage/ui";
import { RiArrowDownSLine } from "@remixicon/react";

import { type SidebarEntry } from "../types";
import { SidebarItem } from "./SidebarItem";
import { useSidebarOpen } from "./sidebarOpenState";
import styles from "./SidebarSubmenu.module.css";

/**
 * A nav rail entry that expands in place to reveal related links.
 *
 * Replaces `SidebarSubmenu` / `SidebarSubmenuItem` from
 * `@backstage/core-components` with a click-to-expand disclosure rather than
 * the original's hover-triggered panel — see the migration notes in
 * `docs/index.md` for why.
 *
 * There is deliberately no separate collapsed-rail rendering. The rail expands
 * on hover or focus before anything inside it can be reached, so a
 * collapsed-only affordance — this component previously had a `Menu` flyout —
 * can never actually be used. Collapsed, the trigger is therefore icon-only and
 * inert, and carries its name via `aria-label` for assistive technology that
 * reads the rail without moving focus.
 *
 * The trigger and its panel form a proper disclosure: `aria-expanded` reports
 * the state and `aria-controls` names the panel, so assistive technology can
 * tell the two apart and jump between them. That requires the panel to exist
 * for `aria-controls` to point at, so while the rail is open it stays mounted
 * and `hidden` does the collapsing. It is dropped entirely once the rail
 * collapses, where the trigger is inert and reports neither attribute.
 *
 * This is deliberately not BUI's `Accordion`, which would supply the same
 * wiring: `AccordionTrigger` wraps its content in a heading, and a heading per
 * submenu would put nav labels in the document outline alongside real page
 * headings. The ARIA disclosure-navigation pattern calls for a plain button.
 */
export const SidebarSubmenu = (props: {
    icon: ReactElement;
    text: string;
    entries: SidebarEntry[];
}) => {
    const { icon, text, entries } = props;
    const isOpen = useSidebarOpen();
    const [isExpanded, setIsExpanded] = useState(false);

    // Ties the trigger to its panel via `aria-controls`.
    const panelId = useId();

    // Collapse the disclosure whenever the rail collapses, so re-expanding the
    // rail does not restore a submenu the user left open some time ago.
    useEffect(() => {
        if (!isOpen) {
            setIsExpanded(false);
        }
    }, [isOpen]);

    return (
        <>
            <button
                type="button"
                className={styles.trigger}
                data-open={isOpen}
                aria-label={isOpen ? undefined : text}
                aria-expanded={isOpen ? isExpanded : undefined}
                aria-controls={isOpen ? panelId : undefined}
                onClick={() => setIsExpanded(expanded => !expanded)}
            >
                <span className={styles.icon}>{icon}</span>
                {isOpen && (
                    <>
                        <Text variant="body-medium" className={styles.label}>
                            {text}
                        </Text>
                        <RiArrowDownSLine
                            size={16}
                            aria-hidden
                            className={styles.chevron}
                        />
                    </>
                )}
            </button>
            {isOpen && (
                <div
                    id={panelId}
                    className={styles.children}
                    hidden={!isExpanded}
                >
                    {entries.map(entry => (
                        <SidebarItem
                            key={entry.href}
                            href={entry.href}
                            text={entry.title}
                            icon={entry.icon}
                        />
                    ))}
                </div>
            )}
        </>
    );
};
